/**
 * Farm Comparison Agent
 * Provides detailed comparisons between farms, products, and farming practices
 */

interface FarmProfile {
  id: string;
  name: string;
  type: string;
  location: { city: string; distance: number };
  products: string[];
  practices: string[];
  certifications: string[];
  pricing: 'budget' | 'moderate' | 'premium';
  rating: number;
  reviewCount: number;
  sustainability: number;
  amenities: string[];
}

interface ComparisonResult {
  farms: FarmProfile[];
  winner: {
    overall: string;
    price: string;
    quality: string;
    sustainability: string;
    convenience: string;
  };
  categories: {
    name: string;
    scores: { farmId: string; score: number; details: string }[];
  }[];
  recommendations: string[];
}

interface ProductComparison {
  product: string;
  farms: Array<{
    farmId: string;
    farmName: string;
    available: boolean;
    price?: number;
    quality: 'premium' | 'standard' | 'value';
    varieties: string[];
    organic: boolean;
  }>;
  bestForPrice: string;
  bestForQuality: string;
  bestOverall: string;
}

interface PracticeComparison {
  practice: string;
  description: string;
  farms: Array<{
    farmId: string;
    farmName: string;
    implements: boolean;
    level: 'full' | 'partial' | 'none';
    details?: string;
  }>;
  benefits: string[];
  environmentalImpact: 'high' | 'medium' | 'low';
}

class FarmComparisonAgent {
  /**
   * Compare multiple farms across key categories
   * @param farmIds Array of farm IDs to compare
   * @param userPreferences User priorities
   * @returns Detailed comparison
   */
  compareFarms(
    farmIds: string[],
    userPreferences?: {
      priorityFactors?: ('price' | 'quality' | 'sustainability' | 'convenience' | 'variety')[];
      location?: { lat: number; lng: number };
      budget?: 'low' | 'medium' | 'high';
    }
  ): ComparisonResult {
    // Simulate farm data (in production, would fetch from database)
    const farms = farmIds.map(id => this.getFarmProfile(id));

    // Score farms in each category
    const categories = [
      this.compareProductVariety(farms),
      this.comparePricing(farms),
      this.compareSustainability(farms),
      this.compareQuality(farms),
      this.compareConvenience(farms, userPreferences?.location),
    ];

    // Determine winners
    const winner = {
      overall: this.determineOverallWinner(farms, categories, userPreferences),
      price: this.determineCategoryWinner(categories, 'Pricing'),
      quality: this.determineCategoryWinner(categories, 'Quality'),
      sustainability: this.determineCategoryWinner(categories, 'Sustainability'),
      convenience: this.determineCategoryWinner(categories, 'Convenience'),
    };

    // Generate recommendations
    const recommendations = this.generateRecommendations(farms, winner, userPreferences);

    return {
      farms,
      winner,
      categories,
      recommendations,
    };
  }

  /**
   * Compare product availability and quality across farms
   * @param product Product name
   * @param farmIds Farm IDs to compare
   * @returns Product comparison
   */
  compareProduct(product: string, farmIds: string[]): ProductComparison {
    const farms = farmIds.map(id => {
      const farm = this.getFarmProfile(id);
      const hasProduct = farm.products.some(p =>
        p.toLowerCase().includes(product.toLowerCase())
      );

      return {
        farmId: id,
        farmName: farm.name,
        available: hasProduct,
        price: hasProduct ? this.estimatePrice(product, farm.pricing) : undefined,
        quality: this.determineQuality(farm.rating, farm.certifications),
        varieties: hasProduct ? this.getVarieties(product) : [],
        organic: farm.certifications.includes('Organic'),
      };
    }).filter(f => f.available);

    // Determine best options
    const bestForPrice = this.findBestPrice(farms);
    const bestForQuality = this.findBestQuality(farms);
    const bestOverall = this.findBestOverall(farms);

    return {
      product,
      farms,
      bestForPrice,
      bestForQuality,
      bestOverall,
    };
  }

  /**
   * Compare farming practices across farms
   * @param practice Practice name (e.g., 'Organic', 'No-Till')
   * @param farmIds Farm IDs to compare
   * @returns Practice comparison
   */
  comparePractice(practice: string, farmIds: string[]): PracticeComparison {
    const farms = farmIds.map(id => {
      const farm = this.getFarmProfile(id);
      const hasPractice = farm.practices.some(p =>
        p.toLowerCase().includes(practice.toLowerCase())
      );

      return {
        farmId: id,
        farmName: farm.name,
        implements: hasPractice,
        level: this.determinePracticeLevel(farm, practice),
        details: hasPractice ? this.getPracticeDetails(farm, practice) : undefined,
      };
    });

    return {
      practice,
      description: this.getPracticeDescription(practice),
      farms,
      benefits: this.getPracticeBenefits(practice),
      environmentalImpact: this.getEnvironmentalImpact(practice),
    };
  }

  /**
   * Get side-by-side comparison table data
   * @param farmIds Farm IDs to compare
   * @returns Structured comparison data
   */
  getComparisonTable(farmIds: string[]): {
    headers: string[];
    rows: Array<{ label: string; values: any[] }>;
  } {
    const farms = farmIds.map(id => this.getFarmProfile(id));

    const headers = ['Feature', ...farms.map(f => f.name)];

    const rows = [
      { label: 'Type', values: farms.map(f => f.type) },
      { label: 'Location', values: farms.map(f => f.location.city) },
      { label: 'Distance', values: farms.map(f => `${f.location.distance} miles`) },
      { label: 'Products', values: farms.map(f => f.products.length) },
      { label: 'Rating', values: farms.map(f => `${f.rating}/5 (${f.reviewCount} reviews)`) },
      { label: 'Pricing', values: farms.map(f => this.formatPricing(f.pricing)) },
      { label: 'Sustainability', values: farms.map(f => `${f.sustainability}/100`) },
      { label: 'Organic Certified', values: farms.map(f => f.certifications.includes('Organic') ? 'Yes' : 'No') },
      { label: 'Amenities', values: farms.map(f => f.amenities.join(', ') || 'None listed') },
    ];

    return { headers, rows };
  }

  /**
   * Find alternative farms with similar offerings
   * @param farmId Source farm ID
   * @param maxDistance Maximum distance in miles
   * @returns Alternative farm suggestions
   */
  findAlternatives(farmId: string, maxDistance: number = 25): Array<{
    farm: FarmProfile;
    similarity: number;
    differences: string[];
    advantages: string[];
  }> {
    const sourceFarm = this.getFarmProfile(farmId);
    const allFarms = this.getAllFarms().filter(f => f.id !== farmId);

    const alternatives = allFarms
      .filter(f => f.location.distance <= maxDistance)
      .map(farm => {
        const similarity = this.calculateSimilarity(sourceFarm, farm);
        const differences = this.identifyDifferences(sourceFarm, farm);
        const advantages = this.identifyAdvantages(sourceFarm, farm);

        return { farm, similarity, differences, advantages };
      })
      .filter(alt => alt.similarity >= 50)
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5);

    return alternatives;
  }

  // Helper Methods

  private getFarmProfile(farmId: string): FarmProfile {
    // Simulated farm data
    const farms: Record<string, Partial<FarmProfile>> = {
      'farm-1': {
        name: 'Suncoast Family Farm',
        type: 'Family Farm',
        location: { city: 'Fort Myers', distance: 5 },
        products: ['Tomatoes', 'Lettuce', 'Peppers', 'Herbs'],
        practices: ['Organic', 'Crop Rotation', 'Composting'],
        certifications: ['Organic', 'Florida Organic Growers'],
        pricing: 'moderate',
        rating: 4.8,
        reviewCount: 156,
        sustainability: 85,
        amenities: ['Farm Stand', 'Tours', 'U-Pick'],
      },
      'farm-2': {
        name: 'Green Valley Organics',
        type: 'Organic Farm',
        location: { city: 'Naples', distance: 12 },
        products: ['Lettuce', 'Kale', 'Strawberries', 'Blueberries'],
        practices: ['Organic', 'No-Till', 'Cover Cropping'],
        certifications: ['USDA Organic', 'Certified Naturally Grown'],
        pricing: 'premium',
        rating: 4.9,
        reviewCount: 203,
        sustainability: 92,
        amenities: ['CSA', 'Delivery', 'Online Ordering'],
      },
      'farm-3': {
        name: 'Value Fresh Farms',
        type: 'Commercial Farm',
        location: { city: 'Bonita Springs', distance: 8 },
        products: ['Tomatoes', 'Cucumbers', 'Squash', 'Peppers'],
        practices: ['Integrated Pest Management', 'Drip Irrigation'],
        certifications: [],
        pricing: 'budget',
        rating: 4.2,
        reviewCount: 89,
        sustainability: 62,
        amenities: ['Farm Stand', 'Bulk Pricing'],
      },
    };

    const profile = farms[farmId] || farms['farm-1'];
    return { id: farmId, ...profile } as FarmProfile;
  }

  private getAllFarms(): FarmProfile[] {
    return ['farm-1', 'farm-2', 'farm-3', 'farm-4', 'farm-5'].map(id => this.getFarmProfile(id));
  }

  private compareProductVariety(farms: FarmProfile[]): ComparisonResult['categories'][0] {
    return {
      name: 'Product Variety',
      scores: farms.map(farm => ({
        farmId: farm.id,
        score: Math.min(100, farm.products.length * 10),
        details: `${farm.products.length} different products`,
      })),
    };
  }

  private comparePricing(farms: FarmProfile[]): ComparisonResult['categories'][0] {
    return {
      name: 'Pricing',
      scores: farms.map(farm => {
        const score = farm.pricing === 'budget' ? 100 : farm.pricing === 'moderate' ? 75 : 50;
        return {
          farmId: farm.id,
          score,
          details: `${this.formatPricing(farm.pricing)} pricing`,
        };
      }),
    };
  }

  private compareSustainability(farms: FarmProfile[]): ComparisonResult['categories'][0] {
    return {
      name: 'Sustainability',
      scores: farms.map(farm => ({
        farmId: farm.id,
        score: farm.sustainability,
        details: `${farm.practices.length} sustainable practices`,
      })),
    };
  }

  private compareQuality(farms: FarmProfile[]): ComparisonResult['categories'][0] {
    return {
      name: 'Quality',
      scores: farms.map(farm => ({
        farmId: farm.id,
        score: (farm.rating / 5) * 100,
        details: `${farm.rating}/5 stars (${farm.reviewCount} reviews)`,
      })),
    };
  }

  private compareConvenience(
    farms: FarmProfile[],
    userLocation?: { lat: number; lng: number }
  ): ComparisonResult['categories'][0] {
    return {
      name: 'Convenience',
      scores: farms.map(farm => {
        let score = 50;
        if (farm.location.distance < 10) score += 30;
        else if (farm.location.distance < 20) score += 15;

        if (farm.amenities.includes('Delivery')) score += 20;

        return {
          farmId: farm.id,
          score: Math.min(100, score),
          details: `${farm.location.distance} miles away, ${farm.amenities.length} amenities`,
        };
      }),
    };
  }

  private determineOverallWinner(
    farms: FarmProfile[],
    categories: ComparisonResult['categories'],
    preferences?: any
  ): string {
    const scores = new Map<string, number>();

    for (const farm of farms) {
      let totalScore = 0;
      for (const category of categories) {
        const farmScore = category.scores.find(s => s.farmId === farm.id);
        totalScore += farmScore?.score || 0;
      }
      scores.set(farm.id, totalScore);
    }

    const winner = Array.from(scores.entries()).sort((a, b) => b[1] - a[1])[0];
    return farms.find(f => f.id === winner[0])?.name || farms[0].name;
  }

  private determineCategoryWinner(categories: ComparisonResult['categories'], categoryName: string): string {
    const category = categories.find(c => c.name === categoryName);
    if (!category) return 'N/A';

    const winner = category.scores.sort((a, b) => b.score - a.score)[0];
    const farm = this.getFarmProfile(winner.farmId);
    return farm.name;
  }

  private generateRecommendations(
    farms: FarmProfile[],
    winner: ComparisonResult['winner'],
    preferences?: any
  ): string[] {
    const recs: string[] = [];

    if (preferences?.budget === 'low') {
      const budgetFarms = farms.filter(f => f.pricing === 'budget');
      if (budgetFarms.length > 0) {
        recs.push(`For budget-conscious shopping, consider ${budgetFarms[0].name}`);
      }
    }

    if (preferences?.priorityFactors?.includes('sustainability')) {
      const topSustainable = farms.sort((a, b) => b.sustainability - a.sustainability)[0];
      recs.push(`For sustainability, ${topSustainable.name} leads with a score of ${topSustainable.sustainability}/100`);
    }

    recs.push(`Overall best choice: ${winner.overall}`);

    return recs;
  }

  private estimatePrice(product: string, pricing: FarmProfile['pricing']): number {
    const basePrices: Record<string, number> = {
      tomatoes: 3.5,
      lettuce: 2.5,
      strawberries: 5.0,
      peppers: 4.0,
    };

    const base = basePrices[product.toLowerCase()] || 3.0;
    const multiplier = pricing === 'budget' ? 0.8 : pricing === 'premium' ? 1.3 : 1.0;

    return Math.round(base * multiplier * 100) / 100;
  }

  private determineQuality(rating: number, certifications: string[]): ProductComparison['farms'][0]['quality'] {
    if (rating >= 4.7 || certifications.includes('Organic')) return 'premium';
    if (rating >= 4.0) return 'standard';
    return 'value';
  }

  private getVarieties(product: string): string[] {
    const varieties: Record<string, string[]> = {
      tomatoes: ['Cherry', 'Beefsteak', 'Roma', 'Heirloom'],
      lettuce: ['Romaine', 'Butterhead', 'Oak Leaf'],
      strawberries: ['Chandler', 'Festival', 'Camino Real'],
    };
    return varieties[product.toLowerCase()] || ['Standard'];
  }

  private findBestPrice(farms: ProductComparison['farms']): string {
    const sorted = farms.filter(f => f.price).sort((a, b) => (a.price || 0) - (b.price || 0));
    return sorted[0]?.farmName || 'N/A';
  }

  private findBestQuality(farms: ProductComparison['farms']): string {
    const premium = farms.filter(f => f.quality === 'premium');
    return premium[0]?.farmName || farms[0]?.farmName || 'N/A';
  }

  private findBestOverall(farms: ProductComparison['farms']): string {
    // Balance of quality and price
    const scores = farms.map(f => ({
      name: f.farmName,
      score: (f.quality === 'premium' ? 100 : f.quality === 'standard' ? 75 : 50) -
             ((f.price || 3) * 10),
    }));
    const best = scores.sort((a, b) => b.score - a.score)[0];
    return best?.name || 'N/A';
  }

  private determinePracticeLevel(farm: FarmProfile, practice: string): PracticeComparison['farms'][0]['level'] {
    if (farm.certifications.some(c => c.toLowerCase().includes(practice.toLowerCase()))) {
      return 'full';
    }
    if (farm.practices.some(p => p.toLowerCase().includes(practice.toLowerCase()))) {
      return 'partial';
    }
    return 'none';
  }

  private getPracticeDetails(farm: FarmProfile, practice: string): string {
    return `${farm.name} implements ${practice} as part of their farming practices`;
  }

  private getPracticeDescription(practice: string): string {
    const descriptions: Record<string, string> = {
      organic: 'Farming without synthetic pesticides or fertilizers',
      'no-till': 'Avoiding soil disturbance to preserve structure and biology',
      'cover cropping': 'Planting crops to improve soil health between main crops',
    };
    return descriptions[practice.toLowerCase()] || 'Sustainable farming practice';
  }

  private getPracticeBenefits(practice: string): string[] {
    const benefits: Record<string, string[]> = {
      organic: ['No synthetic chemicals', 'Better soil health', 'Higher nutrient density'],
      'no-till': ['Prevents erosion', 'Improves water retention', 'Sequesters carbon'],
      'cover cropping': ['Adds organic matter', 'Suppresses weeds', 'Prevents erosion'],
    };
    return benefits[practice.toLowerCase()] || ['Promotes sustainable agriculture'];
  }

  private getEnvironmentalImpact(practice: string): PracticeComparison['environmentalImpact'] {
    const impacts: Record<string, PracticeComparison['environmentalImpact']> = {
      organic: 'high',
      'no-till': 'high',
      'cover cropping': 'high',
      'drip irrigation': 'medium',
    };
    return impacts[practice.toLowerCase()] || 'medium';
  }

  private formatPricing(pricing: FarmProfile['pricing']): string {
    return pricing.charAt(0).toUpperCase() + pricing.slice(1);
  }

  private calculateSimilarity(farm1: FarmProfile, farm2: FarmProfile): number {
    let score = 0;

    // Type similarity
    if (farm1.type === farm2.type) score += 20;

    // Product overlap
    const commonProducts = farm1.products.filter(p => farm2.products.includes(p));
    score += (commonProducts.length / Math.max(farm1.products.length, farm2.products.length)) * 30;

    // Practice overlap
    const commonPractices = farm1.practices.filter(p => farm2.practices.includes(p));
    score += (commonPractices.length / Math.max(farm1.practices.length, farm2.practices.length)) * 25;

    // Pricing similarity
    if (farm1.pricing === farm2.pricing) score += 15;

    // Rating similarity
    const ratingDiff = Math.abs(farm1.rating - farm2.rating);
    score += Math.max(0, 10 - ratingDiff * 2);

    return Math.round(score);
  }

  private identifyDifferences(farm1: FarmProfile, farm2: FarmProfile): string[] {
    const diffs: string[] = [];

    if (farm1.pricing !== farm2.pricing) {
      diffs.push(`Different pricing: ${farm1.name} is ${farm1.pricing}, ${farm2.name} is ${farm2.pricing}`);
    }

    const uniqueProducts = farm2.products.filter(p => !farm1.products.includes(p));
    if (uniqueProducts.length > 0) {
      diffs.push(`Also offers: ${uniqueProducts.slice(0, 3).join(', ')}`);
    }

    const uniquePractices = farm2.practices.filter(p => !farm1.practices.includes(p));
    if (uniquePractices.length > 0) {
      diffs.push(`Additional practices: ${uniquePractices.slice(0, 2).join(', ')}`);
    }

    return diffs;
  }

  private identifyAdvantages(sourceFarm: FarmProfile, altFarm: FarmProfile): string[] {
    const advantages: string[] = [];

    if (altFarm.location.distance < sourceFarm.location.distance) {
      advantages.push('Closer to you');
    }

    if (altFarm.rating > sourceFarm.rating) {
      advantages.push('Higher customer rating');
    }

    if (altFarm.sustainability > sourceFarm.sustainability) {
      advantages.push('More sustainable practices');
    }

    if (altFarm.amenities.length > sourceFarm.amenities.length) {
      advantages.push('More amenities available');
    }

    return advantages;
  }
}

export const farmComparisonAgent = new FarmComparisonAgent();
export default farmComparisonAgent;
